/** Marks synthetic contextmenu events we re-dispatch onto the canvas. */
const BOARD_CONTEXT_MENU_FLAG = '__boardContextMenu';

export function isSyntheticBoardContextMenu(event) {
  return Boolean(event?.[BOARD_CONTEXT_MENU_FLAG]);
}

/** Open tldraw's canvas context menu at viewport coordinates. */
export function openTldrawContextMenuAt(editor, clientX, clientY) {
  const canvas = editor.getContainer()?.querySelector('.tl-canvas');
  if (!canvas) return;
  const event = new MouseEvent('contextmenu', {
    clientX,
    clientY,
    bubbles: true,
    cancelable: true,
  });
  event[BOARD_CONTEXT_MENU_FLAG] = true;
  canvas.dispatchEvent(event);
}

function selectShapeForContextMenu(editor, shapeId) {
  const selected = editor.getSelectedShapeIds?.() || [];
  // Right-clicking a shape that is already part of a multi-selection keeps
  // that selection. Right-clicking a different object replaces it.
  if (selected.length > 1 && selected.includes(shapeId)) return;
  if (selected.length === 1 && selected[0] === shapeId) return;
  editor.select(shapeId);
}

export function syncContextMenuPagePoint(editor, clientX, clientY) {
  const pagePoint = editor.screenToPage?.({ x: clientX, y: clientY });
  if (!pagePoint || !editor.inputs?.currentPagePoint?.set) return;
  editor.inputs.currentPagePoint.set(pagePoint.x, pagePoint.y);
}

function hitShapeAtClientPoint(editor, clientX, clientY) {
  const pagePoint = editor.screenToPage?.({ x: clientX, y: clientY });
  if (!pagePoint) return null;
  const precise = editor.getShapeAtPoint?.(pagePoint, { hitInside: true, margin: 8 });
  if (precise) return precise;
  const selectedCount = editor.getSelectedShapeIds?.()?.length || 0;
  if (selectedCount > 1) return null;
  return editor.getShapeAtPoint?.(pagePoint, {
    hitInside: true,
    hitFrameInside: true,
    hitLabels: true,
    margin: 8,
  }) || null;
}

/** Shape element under the cursor. Frame titles are HTML and sit outside the body hit area. */
function shapeFromContextMenuTarget(editor, event) {
  const id = event?.target?.closest?.('[data-shape-id]')?.getAttribute?.('data-shape-id');
  if (!id) return null;
  return editor.getShape?.(id) || null;
}

/**
 * Topmost real shape under the cursor. Skips the open-menu dismiss layer, which
 * otherwise hides the object the user just right-clicked.
 */
function shapeFromElementsAtPoint(editor, clientX, clientY) {
  const stack = document.elementsFromPoint?.(clientX, clientY);
  if (!stack?.length) return null;
  for (const el of stack) {
    if (!el?.closest) continue;
    if (el.closest('[data-testid="context-menu"]')) continue;
    if (el.closest('.tlui-menu-click-capture')) continue;
    const shapeEl = el.closest('[data-shape-id]');
    if (!shapeEl) continue;
    const id = shapeEl.getAttribute('data-shape-id');
    const shape = id ? editor.getShape?.(id) : null;
    if (shape) return shape;
  }
  return null;
}

function shapeUnderClientPoint(editor, clientX, clientY, event) {
  const fromStack = shapeFromElementsAtPoint(editor, clientX, clientY);
  if (fromStack) return fromStack;
  const fromTarget = event ? shapeFromContextMenuTarget(editor, event) : null;
  if (fromTarget?.type === 'frame') return fromTarget;
  return hitShapeAtClientPoint(editor, clientX, clientY) || fromTarget;
}

/**
 * Select a shape (keeping multi-select when it is already selected) and open
 * the board context menu at the cursor.
 */
export function openShapeContextMenu(editor, shapeId, event) {
  if (event?.[BOARD_CONTEXT_MENU_FLAG]) return;
  event?.preventDefault?.();
  event?.stopPropagation?.();
  if (!editor || !shapeId) return;
  selectShapeForContextMenu(editor, shapeId);
  openTldrawContextMenuAt(editor, event.clientX, event.clientY);
}

/** Select a pin shape and open the board context menu at the cursor. */
export function openPinContextMenu(editor, shapeId, event) {
  openShapeContextMenu(editor, shapeId, event);
}

function applyContextMenuTarget(editor, clientX, clientY, event, { clearIfMiss = false } = {}) {
  syncContextMenuPagePoint(editor, clientX, clientY);
  const hit = shapeUnderClientPoint(editor, clientX, clientY, event);
  if (hit) {
    selectShapeForContextMenu(editor, hit.id);
    return hit;
  }
  if (clearIfMiss && editor.getSelectedShapeIds?.().length === 1) {
    editor.selectNone?.();
  }
  return null;
}

function releaseMenuClickCapture(container) {
  const overlay = container.querySelector?.('.tlui-menu-click-capture');
  if (overlay) overlay.style.pointerEvents = '';
}

/**
 * Right-click any canvas object (frame, connect line, text, image, pin, …)
 * opens the board context menu. HTML shapes otherwise swallow contextmenu
 * before Radix on the canvas sees it.
 *
 * While a menu is open, tldraw covers the canvas with .tlui-menu-click-capture.
 * That layer receives the next right-click, so the menu stayed tied to the
 * previously selected object. Select the shape under the cursor first, then
 * let the context menu reopen for that shape.
 */
export function registerCanvasShapeContextMenu(editor) {
  const container = editor.getContainer?.();
  if (!container) return () => {};

  const onPointerDown = event => {
    if (event.button !== 2) return;
    if (event.target?.closest?.('[data-testid="context-menu"]')) return;
    const overlay = event.target?.closest?.('.tlui-menu-click-capture');
    if (!overlay) return;

    // Select the object under the dismiss layer before it closes the menu.
    // pointer-events: none lets the following contextmenu hit that object.
    applyContextMenuTarget(editor, event.clientX, event.clientY, event, {
      clearIfMiss: true,
    });
    overlay.style.pointerEvents = 'none';
  };

  const onContextMenu = event => {
    releaseMenuClickCapture(container);
    if (event[BOARD_CONTEXT_MENU_FLAG]) return;
    if (editor.getEditingShapeId?.()) return;
    if (event.target?.closest?.('[data-testid="context-menu"]')) return;

    const hit = applyContextMenuTarget(editor, event.clientX, event.clientY, event);
    if (!hit) return;

    event.preventDefault();
    event.stopPropagation();
    openTldrawContextMenuAt(editor, event.clientX, event.clientY);
  };

  container.addEventListener('pointerdown', onPointerDown, true);
  container.addEventListener('contextmenu', onContextMenu, true);
  return () => {
    container.removeEventListener('pointerdown', onPointerDown, true);
    container.removeEventListener('contextmenu', onContextMenu, true);
    releaseMenuClickCapture(container);
  };
}
